import cuid from "cuid";
import { find, get, isEmpty } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { GENERATE_SUBSCRIPTION_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { DOWNLOAD_SINGLE_DOCUMENT } from "utils/api/graphql/mutations/download-document";
import { SIGN_DOCUMENT } from "utils/api/graphql/mutations/esign";
import { SUBSCRIPTION } from "utils/api/graphql/queries/subscription";
import {
  KEYS,
  ACTION_TYPES,
  DOCUMENT_STEPS,
  DOCUMENTS_TYPES,
  INSURANCE_PLAN_ATTACHMENTS_TYPES,
  PROCESS_TYPES,
  SCREENS,
  TEMPLATES,
  TEMPLATE_TYPES,
} from "utils/constants";
import { filterAttachmentsByStep } from "utils/helpers/documentStepsConfig";
import { downloadDocument } from "utils/helpers/files";
import { filterByConditions } from "../../../..";
import Loading from "../Components/Spin";
import Template from "../Template";
import View from "./View";

const ATTACHMENTS_TYPES = [
  DOCUMENTS_TYPES.MEMBERSHIP_FORM,
  DOCUMENTS_TYPES.DUTY_OF_ADVICE,
  DOCUMENTS_TYPES.SEPA_DIRECT_DEBIT_MANDATE,
  DOCUMENTS_TYPES.SUPPLEMENTARY_DOCUMENTS,
  DOCUMENTS_TYPES.QUOTE,
  DOCUMENTS_TYPES.DUTY_OF_ADVICE_QUOTE,
  DOCUMENTS_TYPES.TERMINATION_LETTER,
];

const Contract = ({
  currentStatus,
  tracer,
  project,
  onBack,
  onNext,
  updateUrl,
}) => {
  const [updateProject] = useMutation(graphql.mutations.UPDATE_PROJECT);
  const [generateSubscriptionDocuments] = useMutation(
    GENERATE_SUBSCRIPTION_DOCUMENTS,
  );
  const [loading, setLoading] = useState(true);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const subscriptionId =
    get(project, "fields.currentSubscription") ||
    localStorage.getItem(KEYS.SUBSCRIPTION_ID);

  const navigate = useNavigate();
  const [getAttachments, { data: attachmentsList }] = useLazyQuery(
    graphql.queries.ATTACHMENTS,
  );
  const [downloadSingleDocument] = useMutation(DOWNLOAD_SINGLE_DOCUMENT);

  const { data } = useQuery(graphql.queries.SUBSCRIPTION, {
    fetchPolicy: "no-cache",
    variables: { where: { id: subscriptionId } },
    onCompleted: ({ subscription: subscriptionData }) => {
      getAttachments({
        fetchPolicy: "no-cache",
        variables: {
          isIn: {
            type: [
              ...Object.values(INSURANCE_PLAN_ATTACHMENTS_TYPES),
              ...ATTACHMENTS_TYPES,
            ],
          },
          where: {
            OR: [
              {
                AND: [
                  {
                    project: {
                      id: project.id,
                    },
                  },
                  {
                    insurancePlans: {
                      id: get(subscriptionData, "insurancePlan.id", ""),
                    },
                  },
                ],
              },
              {
                subscription: {
                  id: subscriptionId,
                },
              },
              {
                AND: [
                  {
                    project: null,
                  },
                  {
                    insurancePlans: {
                      id: get(subscriptionData, "insurancePlan.id", ""),
                    },
                  },
                ],
              },
            ],
          },
        },
        onCompleted: () => {
          setLoading(false);
        },
      });
    },
  });

  const download = async (id) => {
    setLoading(true);
    await downloadSingleDocument({
      variables: { where: { id } },
      onCompleted: (_) => {
        const { document } = omitDeep(_.downloadSingleDocument, "__typename");
        downloadDocument(document);
        setLoading(false);
      },
    });
  };

  const [signDocuments] = useMutation(SIGN_DOCUMENT, {
    refetchQueries: [
      {
        query: SUBSCRIPTION,
        awaitRefetchQueries: true,
        variables: { where: { id: subscriptionId } },
      },
    ],
  });

  const { data: generalCost } = useQuery(graphql.queries.GENERAL_COST, {
    fetchPolicy: "no-cache",
    variables: {
      where: {
        project: { id: get(project, "id") },
        insurancePlan: {
          id: get(data, "subscription.insurancePlan.id"),
        },
      },
    },
    onCompleted: () => setLoading(false),
  });

  const findStatus = (data, template) => {
    return find(data, ({ template: _ }) => _ === template);
  };

  const updateProjectStatus = ({ template }) => {
    const status = findStatus(
      get(tracer, "flow.process.processStatuses", []),
      template,
    );
    const actionsToExecute = filterByConditions(
      get(status, "actions", []),
      project,
    );
    const syncActions = actionsToExecute
      .filter(({ type }) => type === ACTION_TYPES.SYNC_PROCESSES)
      .map(({ args: { status } }) => ({
        id: cuid(),
        status: { id: status },
      }));
    updateProject({
      variables: {
        where: { id: project.id },
        data: {
          statuses: [{ id: cuid(), status: { id: status.id } }, ...syncActions],
        },
      },
      onCompleted: ({ updateProject }) => {
        navigate(
          `${SCREENS.PRIVATE.GUEST.PROJECTS.DETAIL.path}/${updateProject?.id}`,
          {
            state: {
              process: get(omitDeep(tracer, "__typename"), "flow.process"),
              tracer: omitDeep(tracer, "__typename"),
            },
          },
        );
      },
    });
  };

  const onEdit = (v) => {
    updateProjectStatus({
      template: TEMPLATES[PROCESS_TYPES.B2C].THEMES[TEMPLATE_TYPES.B2C_LMF][v],
    });
  };

  const onSubmit = async ({ contact: { fields } }) => {
    setDocumentsLoading(true);
    updateProject({
      variables: {
        where: { id: project.id },
        data: {
          contact: {
            id: get(project, "contact.id"),
            fields: { ...get(project, "contact.fields", {}), ...fields },
          },
        },
      },
      onCompleted: () => {
        generateSubscriptionDocuments({
          variables: {
            data: { id: subscriptionId },
          },
          onCompleted: () =>
            signDocuments({
              variables: {
                data: { id: subscriptionId },
              },
              onCompleted: () => {
                const source = localStorage.getItem(KEYS.SOURCE);
                updateUrl({
                  subscriptionId,
                  url: get(
                    data,
                    "subscription.insurancePlan.config.generatedUrl",
                    `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
                  ),
                });
                onNext({
                  payload: {},
                  onCompleted: () => setDocumentsLoading(false),
                });
              },
            }),
        });
      },
    });
  };

  if (isEmpty(get(data, "subscription"))) return <Loading />;

  return (
    <Template
      progress={currentStatus.progress}
      onBack={onBack}
      loading={loading}
      project={project}
      HeaderComponent={{
        subscription: get(data, "subscription"),
        tracer,
        project,
        contact: get(project, "contact"),
      }}
      View={
        <View
          download={download}
          attachments={filterAttachmentsByStep({
            attachments: get(attachmentsList, "attachments.data", []),
            params: get(data, "subscription.insurancePlan.params", []),
            step: DOCUMENT_STEPS.CONTRAT,
          })}
          locked={get(data, "subscription.locked", false)}
          onSubmit={onSubmit}
          project={project}
          subscription={get(data, "subscription")}
          generalCost={get(generalCost, "generalCost")}
          onEdit={onEdit}
          onBack={onBack}
          loading={documentsLoading}
        />
      }
      contact={get(project, "contact")}
    />
  );
};
export default Contract;
